// The Apartments sheet's draft (docs/16): the shared sheet engine with the Tayasim fields and any
// number of parking and storage slots. Each slot of a row holds one allocation of that apartment;
// the allocation's id stays with the slot (hidden), so changes name records, never positions.
import {
  APARTMENT_CELL_FIELDS,
  APARTMENT_CLEARABLE,
  APARTMENT_COLUMNS,
  APARTMENT_PERSONAL_FIELDS,
  PERSONAL_FIELD_LIMITS,
  APARTMENT_SOURCE_COLUMNS,
  type AllocatedSpace,
  type ApartmentBatchRowInput,
  type ApartmentCellField,
  type ApartmentClearable,
  type ApartmentSheetRow,
  type SlotChange,
  type SpaceSlotKind,
  SOURCE_NUMBER_MAX,
  type StructureListResponse,
  isAvailableColumn,
  parseSlotKey,
  slotKey,
} from '@sivan/shared';
import { type SheetRow as GenericRow, draftEngine } from './sheet-draft';

export type { EditRefusal, RowState } from './sheet-draft';
export type Structures = StructureListResponse['items'];
/** Field keys: the apartment's own fields and slot columns ("parkingNumber", "parkingNumber_2"). */
export type SheetRow = GenericRow<string, string>;

const KINDS: readonly SpaceSlotKind[] = ['parking', 'storage'];

/** Hidden base key holding the allocation id a slot shows. */
const allocationKey = (kind: SpaceSlotKind, slot: number) => `${kind}Allocation:${String(slot)}`;

const isClearable = (key: string): key is ApartmentClearable =>
  (APARTMENT_CLEARABLE as readonly string[]).includes(key);

/**
 * The explicit clear an emptied field means (null: building, apartment number and the original
 * apartment number cannot be emptied).
 */
export function clearKeyOf(field: string): string | null {
  if (isClearable(field)) return field;
  const slot = parseSlotKey(field);
  return slot === null ? null : `${slot.kind}:${String(slot.slot)}`;
}

export const kindOf = (field: string): SpaceSlotKind | undefined => parseSlotKey(field)?.kind;

// The draft tracks every writable field, personal ones included; the grid decides who may edit.
const isField = (key: string): key is string =>
  isAvailableColumn(key, { contact: true, note: true });

/** Immediate feedback with the rules the server applies (it re-validates everything). */
export function validateRow(row: SheetRow, structures: Structures): Record<string, string> {
  const errors: Record<string, string> = {};
  const value = (f: string) => engine.displayValue(row, f).trim();
  const limits: Readonly<Record<ApartmentCellField, number>> = {
    building: 12,
    apartmentNumber: 12,
    floor: 40,
    modelType: 40,
    sourceNumber: SOURCE_NUMBER_MAX,
    sourceFloorLabel: 40,
  };
  for (const field of APARTMENT_CELL_FIELDS) {
    if (row.edits[field] !== undefined && value(field).length > limits[field])
      errors[field] = 'too_long';
  }
  for (const field of APARTMENT_PERSONAL_FIELDS) {
    if (row.edits[field] !== undefined && value(field).length > PERSONAL_FIELD_LIMITS[field])
      errors[field] = 'too_long';
  }
  if (row.unitId === null) {
    if (value('building') === '') errors['building'] ??= 'required';
    if (value('apartmentNumber') === '') errors['apartmentNumber'] ??= 'required';
  }
  const buildings = structures.filter((s) => s.kind !== 'parking' && s.kind !== 'storage');
  const code = value('building').toLowerCase();
  const structure = buildings.find((s) => s.code.toLowerCase() === code);
  if (row.edits['building'] !== undefined && structure === undefined)
    errors['building'] ??= 'unknown_building';
  const floor = value('floor');
  if (structure !== undefined && row.edits['floor'] !== undefined && floor !== '') {
    const matches = structure.levels.filter(
      (l) => l.label.toLowerCase() === floor.toLowerCase() || String(l.number) === floor,
    );
    if (matches.length === 0) errors['floor'] ??= 'unknown_floor';
  }
  for (const key of Object.keys(row.edits)) {
    const slot = parseSlotKey(key);
    if (slot === null) continue;
    const number = slotKey(slot.kind, 'number', slot.slot);
    if (value(key).length > (slot.part === 'number' ? 12 : 40)) errors[number] ??= 'too_long';
    if (slot.part === 'location' && value(number) === '')
      errors[number] ??= 'location_without_number';
  }
  return errors;
}

const engine = draftEngine<string, string, Structures>({
  columns: [...APARTMENT_COLUMNS, ...APARTMENT_SOURCE_COLUMNS].map((c) => c.key),
  isField,
  clearKey: clearKeyOf,
  validate: validateRow,
});

export const {
  newRow,
  isCleared,
  displayValue,
  rowState,
  changedFields,
  lockedReason,
  setCell,
  clearCell,
  applyPaste,
  applyResult,
  keepMyChange,
  discardRow,
  summarize,
} = engine;

/** How many slots of a kind the row stores (allocations it holds now). */
export const storedSlots = (row: SheetRow, kind: SpaceSlotKind): number =>
  Object.keys(row.base ?? {}).filter((k) => k.startsWith(`${kind}Allocation:`)).length;

/** The highest slot a row uses (stored or edited), +1; 0 when none. */
export function usedSlots(row: SheetRow, kind: SpaceSlotKind): number {
  let used = storedSlots(row, kind);
  for (const key of [...Object.keys(row.edits), ...Object.keys(row.base ?? {})]) {
    const slot = parseSlotKey(key);
    if (slot?.kind === kind && (row.edits[key] ?? row.base?.[key] ?? '') !== '')
      used = Math.max(used, slot.slot + 1);
  }
  return used;
}

/** The batch: changed apartment fields, explicit clears, and one change per touched slot. */
export function toBatchRows(rows: readonly SheetRow[]): ApartmentBatchRowInput[] {
  return rows.flatMap((row): ApartmentBatchRowInput[] => {
    const state = rowState(row);
    if (state === 'stored' || state === 'placeholder') return [];
    const cells = Object.fromEntries(
      [...APARTMENT_CELL_FIELDS, ...APARTMENT_PERSONAL_FIELDS].flatMap((f) =>
        row.edits[f] === undefined ? [] : [[f, row.edits[f]]],
      ),
    );
    const slots: Partial<Record<SpaceSlotKind, SlotChange[]>> = {};
    for (const kind of KINDS) {
      const touched = new Set<number>();
      for (const key of Object.keys(row.edits)) {
        const slot = parseSlotKey(key);
        if (slot?.kind === kind) touched.add(slot.slot);
      }
      for (const clear of row.clears) {
        const [k, n] = clear.split(':');
        if (k === kind && n !== undefined) touched.add(Number(n));
      }
      const changes = [...touched]
        .sort((a, b) => a - b)
        .map((slot): SlotChange => {
          const allocationId = row.base?.[allocationKey(kind, slot)] ?? null;
          if (row.clears.includes(`${kind}:${String(slot)}`))
            return { slot, allocationId, clear: true };
          const number = row.edits[slotKey(kind, 'number', slot)];
          const location = row.edits[slotKey(kind, 'location', slot)];
          return {
            slot,
            allocationId,
            ...(number === undefined ? {} : { number }),
            ...(location === undefined ? {} : { location }),
          };
        });
      if (changes.length > 0) slots[kind] = changes;
    }
    const clear = row.clears.filter(isClearable);
    if (row.unitId === null) return [{ op: 'create', clientRowId: row.key, cells, slots }];
    return [
      {
        op: 'update',
        clientRowId: row.key,
        unitId: row.unitId,
        rowVersion: row.rowVersion ?? 1,
        cells,
        clear,
        slots,
      },
    ];
  });
}

export function rowFromApartment(apartment: ApartmentSheetRow): SheetRow {
  const base: Record<string, string> = {
    building: apartment.building,
    floor: apartment.floor,
    apartmentNumber: apartment.apartmentNumber,
    modelType: apartment.modelType ?? '',
    sourceNumber: apartment.sourceNumber ?? '',
    sourceFloorLabel: apartment.sourceFloorLabel ?? '',
  };
  // Personal values only when the server sent them (the reader may see them, DR-185).
  if (apartment.personal !== null)
    for (const field of APARTMENT_PERSONAL_FIELDS) base[field] = apartment.personal[field] ?? '';
  const slots = (kind: SpaceSlotKind, list: readonly AllocatedSpace[]) => {
    // Each space in the pair it was entered in; earlier pairs may stay empty.
    for (const space of list) {
      base[slotKey(kind, 'number', space.slot)] = space.number;
      base[slotKey(kind, 'location', space.slot)] = space.location;
      base[allocationKey(kind, space.slot)] = space.allocationId;
    }
    // The default pair always exists, empty when the apartment has no space of the kind.
    for (const part of ['number', 'location'] as const) {
      const key = slotKey(kind, part, 0);
      if (!(key in base)) base[key] = '';
    }
  };
  slots('parking', apartment.parking);
  slots('storage', apartment.storage);
  return {
    ...engine.newRow(),
    key: apartment.unitId,
    unitId: apartment.unitId,
    rowVersion: apartment.rowVersion,
    base,
    // The original apartment number is set once (DR-183): a stored one is never edited.
    locked: apartment.sourceNumber === null ? {} : { sourceNumber: 'source_immutable' },
  };
}

/** Stored apartments in server order, followed by the empty "new rows" placeholder. */
export const rowsFromSheet = (items: readonly ApartmentSheetRow[]): SheetRow[] => [
  ...items.map(rowFromApartment),
  engine.newRow(),
];
